// Utility functions for parsing and formatting lab report data

export const parseAnalysis = (analysisText) => {
  const sections = {
    greeting: '',
    healthSummary: '',
    deficiencies: [],
    testResults: [],
    recommendations: [],
    doctorQuestions: [],
    specialists: []
  };

  const lines = analysisText.split('\n');
  let currentSection = '';
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.includes('Personal Greeting') || line.startsWith('Hello') || line.startsWith('Dear')) {
      currentSection = 'greeting';
      if (line.startsWith('Hello') || line.startsWith('Dear')) {
        sections.greeting = line;
      }
      continue;
    } else if (line.includes('Health Status Summary')) {
      currentSection = 'healthSummary';
      continue;
    } else if (line.includes('Detailed Test Results')) {
      currentSection = 'testResults';
      continue;
    } else if (line.includes('Personalized Recommendations')) {
      currentSection = 'recommendations';
      continue;
    } else if (line.includes('Questions for Your Doctor')) {
      currentSection = 'doctorQuestions';
      continue;
    } else if (line.includes('Recommended Healthcare Specialists') || line.includes('Healthcare Specialists')) {
      currentSection = 'specialists';
      continue;
    }
    
    if (line && !line.startsWith('#') && !line.startsWith('###')) {
      if (currentSection === 'greeting' && (line.startsWith('Hello') || line.startsWith('Dear'))) {
        sections.greeting = line;
      } else if (currentSection === 'healthSummary') {
        if (line.includes('good health') || line.includes('normal ranges')) {
          sections.healthSummary = line.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
        } else if (line.includes('need attention') || line.includes('deficiencies')) {
          sections.healthSummary = line.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
        } else if (line.startsWith('- ') || line.startsWith('• ')) {
          let cleanDeficiency = line.substring(2).replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
          sections.deficiencies.push(cleanDeficiency);
        }
      } else if (currentSection === 'testResults' && line.includes(':')) {
        // Parse test results with improved format handling
        const testMatch = line.match(/^-?\s*\*?\*?([^:]+):\s*(.+)/);
        if (testMatch) {
          const testName = testMatch[1].replace(/\*/g, '').trim();
          const fullDetails = testMatch[2];
          
          // Extract value, range, and status
          let value = '';
          let range = '';
          let status = 'normal';
          let explanation = '';
          
          // Try to extract value and range from patterns like "120 mg/dL (70-100 mg/dL) - Status: High"
          const valueRangeMatch = fullDetails.match(/^([^(]+?)(?:\s*\(([^)]+)\))?\s*-?\s*(?:Status:\s*)?(Normal|High|Low|Critical|Elevated|Decreased|Above normal|Below normal|Within normal)/i);
          
          if (valueRangeMatch) {
            value = valueRangeMatch[1].trim();
            range = valueRangeMatch[2] ? `(${valueRangeMatch[2].trim()})` : '';
            const statusText = valueRangeMatch[3].toLowerCase();
            
            if (statusText.includes('high') || statusText.includes('elevated') || statusText.includes('above')) {
              status = 'high';
            } else if (statusText.includes('low') || statusText.includes('decreased') || statusText.includes('below')) {
              status = 'low';
            } else if (statusText.includes('critical')) {
              status = 'critical';
            } else {
              status = 'normal';
            }
          } else {
            // Fallback: use the full details as value
            value = fullDetails;
            
            // Determine status from the full details
            const detailsLower = fullDetails.toLowerCase();
            if (detailsLower.includes('high') || detailsLower.includes('elevated') || detailsLower.includes('above normal')) {
              status = 'high';
            } else if (detailsLower.includes('low') || detailsLower.includes('below normal') || detailsLower.includes('deficient')) {
              status = 'low';
            } else if (detailsLower.includes('critical') || detailsLower.includes('severely')) {
              status = 'critical';
            }
          }
          
          // Look for explanation in the next few lines
          for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
            const nextLine = lines[j].trim();
            if (nextLine && !nextLine.startsWith('- **') && !nextLine.includes(':') && 
                !nextLine.startsWith('#') && !nextLine.startsWith('Resource:') &&
                !nextLine.startsWith('http') && !nextLine.match(/^-?\s*\*?\*?[^:]+:/)) {
              explanation = nextLine;
              break;
            }
          }
          
          sections.testResults.push({ 
            name: testName, 
            value: value,
            range: range,
            status: status,
            explanation: explanation || ''
          });
        }
      } else if (currentSection === 'recommendations' && (line.startsWith('-') || line.match(/^\d+\./))) {
        // Clean up the recommendation text and convert markdown bold to HTML
        let cleanRec = line.replace(/^-?\s*\d*\.?\s*/, '');
        // Convert **text** to <b>text</b>
        cleanRec = cleanRec.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
        sections.recommendations.push(cleanRec);
      } else if (currentSection === 'doctorQuestions' && (line.startsWith('-') || line.match(/^\d+\./))) {
        sections.doctorQuestions.push(line.replace(/^-?\s*\d*\.?\s*/, '').replace(/\*/g, ''));
      } else if (currentSection === 'specialists') {
        // Enhanced specialists parsing
        if (line.startsWith('-') || line.match(/^\d+\./) || line.includes(':') || 
            line.includes('Primary Care') || line.includes('Specialist') || 
            line.includes('Local Healthcare') || line.includes('Physician') ||
            line.includes('Cardiologist') || line.includes('Endocrinologist') ||
            line.includes('Nutritionist') || line.includes('Hematologist') ||
            line.includes('Nephrologist') || line.includes('Gastroenterologist') ||
            line.includes('Rheumatologist') || line.includes('Preventive Care') ||
            line.includes('Wellness Coordinator')) {
          
          let cleanText = line.replace(/^-?\s*\d*\.?\s*/, '');
          
          // Handle different formats like "**Primary Care**: recommendation" or "Primary Care - recommendation"
          if (cleanText.includes(':')) {
            const parts = cleanText.split(':');
            if (parts.length >= 2) {
              const specialistType = parts[0].replace(/\*\*/g, '').trim();
              const recommendation = parts.slice(1).join(':').trim();
              cleanText = `${specialistType}: ${recommendation}`;
            }
          } else if (cleanText.includes(' - ')) {
            // Handle format like "Primary Care - recommendation"
            cleanText = cleanText.replace(' - ', ': ');
          }
          
          cleanText = cleanText.replace(/\*\*/g, '');
          
          if (cleanText.trim()) {
            sections.specialists.push(cleanText);
          }
        }
      }
    }
  }
  
  // Add fallback content for empty sections to ensure good user experience
  if (!sections.greeting) {
    sections.greeting = "Hello! Here's your personalized lab report analysis.";
  }
  
  if (!sections.healthSummary) {
    sections.healthSummary = "Your lab report has been analyzed. Please review the detailed results below.";
  }
  
  if (sections.recommendations.length === 0) {
    sections.recommendations = [
      "Maintain a balanced diet rich in fruits and vegetables",
      "Engage in regular physical activity as recommended by your doctor",
      "Stay hydrated by drinking adequate water daily",
      "Get regular sleep (7-9 hours per night)",
      "Schedule regular follow-up appointments with your healthcare provider"
    ];
  }
  
  if (sections.doctorQuestions.length === 0) {
    sections.doctorQuestions = [
      "What do my specific test results mean for my overall health?",
      "How often should I repeat these tests?",
      "Are there any lifestyle changes I should make based on these results?",
      "Do I need any medications or supplements?",
      "When should I schedule my next appointment?"
    ];
  }
  
  if (sections.specialists.length === 0) {
    sections.specialists = [
      "Primary Care Physician: Schedule a follow-up appointment to discuss your results and create a comprehensive health plan",
      "Preventive Care Specialist: Consider annual wellness visits for ongoing health monitoring and preventive screenings",
      "Nutritionist: Get personalized dietary advice to optimize your health based on your lab results"
    ];
  }
  
  return sections;
};

export const getStatusColor = (status) => {
  switch (status) {
    case 'high':
    case 'low':
    case 'critical':
      return 'abnormal';
    default:
      return 'normal';
  }
};

export const getStatusIcon = (status) => {
  switch (status) {
    case 'normal':
      return '✓';
    case 'high':
    case 'low':
      return '⚠️';
    case 'critical':
      return '🔴';
    default:
      return '✓';
  }
};